/**
 * Pre-Approval Constants
 *
 * Centralized configuration for pre-approval system.
 * All magic numbers and configuration values should be defined here.
 */

export const PRE_APPROVAL_CONSTANTS = {
  /**
   * Expiration and Token Settings
   */
  EXPIRATION: {
    /** Default expiration for pre-approval letters (in days) */
    DEFAULT_DAYS: 30,

    /** Maximum allowed expiration (in days) */
    MAX_DAYS: 90,

    /** Share token validity (in days) */
    SHARE_TOKEN_DAYS: 30,

    /** Share token validity (in hours) - for display */
    SHARE_TOKEN_HOURS: 720 // 30 days * 24 hours
  },

  /**
   * PDF Preview Settings
   */
  PDF_ZOOM: {
    /** Default zoom level for PDF preview */
    DEFAULT: 1.1,

    /** Minimum zoom level */
    MIN: 0.5,

    /** Maximum zoom level */
    MAX: 2.0,

    /** Zoom adjustment step */
    STEP: 0.1
  },

  /**
   * Loan Amount Slider Settings
   */
  SLIDER: {
    /** Step increment for loan amount slider (in dollars) */
    LOAN_AMOUNT_STEP: 1000,

    /** Minimum loan amount as percentage of original */
    MIN_LOAN_PERCENT: 0.5, // 50% of original

    /** Fallback minimum loan amount (in dollars) */
    MIN_LOAN_AMOUNT: 100000
  },

  /**
   * Loan Validation and Calculation Settings
   */
  VALIDATION: {
    /** Maximum Loan-to-Value ratio (conventional) */
    MAX_LTV: 97,

    /** LTV threshold for PMI requirement */
    PMI_THRESHOLD_LTV: 80,

    /** Minimum down payment percentage */
    MIN_DOWN_PAYMENT_PERCENT: 3,

    /** Rounding precision for percentages */
    PERCENT_PRECISION: 1 // 1 decimal place
  },

  /**
   * Form Field Character Limits
   */
  FIELD_LIMITS: {
    /** First/Last name maximum length */
    NAME: 30,

    /** Email maximum length */
    EMAIL: 100,

    /** Address maximum length */
    ADDRESS: 100,

    /** City maximum length */
    CITY: 50,

    /** ZIP code length */
    ZIP: 5
  },

  /**
   * Default Loan Types
   */
  LOAN_TYPES: {
    DEFAULT: 'conventional' as const,
    OPTIONS: [
      'conventional',
      'fha',
      'va',
      'usda',
      'jumbo'
    ] as const
  },

  /**
   * Default Property Settings
   */
  PROPERTY: {
    DEFAULT_TYPE: 'single-family' as const,
    DEFAULT_OCCUPANCY: 'primary' as const,
    TBD_ADDRESS: 'TBD' as const
  },

  /**
   * Template Preview Settings
   */
  PREVIEW: {
    /** Default preview visibility */
    SHOW_BY_DEFAULT: true
  }
} as const;

/**
 * Helper function to calculate expiration date
 * @param days Number of days from now
 * @returns ISO date string (YYYY-MM-DD format)
 */
export function getExpirationDate(days: number = PRE_APPROVAL_CONSTANTS.EXPIRATION.DEFAULT_DAYS): string {
  const date = new Date();
  date.setDate(date.getDate() + days);
  return date.toISOString().split('T')[0];
}

/**
 * Helper function to calculate share token expiration
 * @param days Number of days from now
 * @returns ISO timestamp string
 */
export function getShareTokenExpiration(days: number = PRE_APPROVAL_CONSTANTS.EXPIRATION.SHARE_TOKEN_DAYS): string {
  const date = new Date();
  date.setDate(date.getDate() + days);
  return date.toISOString();
}

/**
 * Helper function to format loan amount with proper step
 * @param amount Raw amount
 * @returns Amount rounded to nearest step
 */
export function roundToLoanStep(amount: number): number {
  const step = PRE_APPROVAL_CONSTANTS.SLIDER.LOAN_AMOUNT_STEP;
  return Math.round(amount / step) * step;
}

/**
 * Helper function to check if PMI is required
 * @param ltv Loan-to-Value ratio
 * @returns true if PMI is required
 */
export function requiresPMI(ltv: number): boolean {
  return ltv > PRE_APPROVAL_CONSTANTS.VALIDATION.PMI_THRESHOLD_LTV;
}
